import { createSlice } from "@reduxjs/toolkit";
import {
  checkinBusinessInvoicesAction,
  checkinBusinessSubscriptionDetailsAction,
  checkinBusinessTopupsAction,
  editMapCheckInFormAction,
  facebookExchangeTokenAction,
  getIntegrationAction,
  instagramExchangeTokenAction,
  instagramPortfolioExchangeTokenAction,
} from "../actions/MapCheckInDashboardAction";
import {
  IntigrationAction,
  removeIntigrationAction,
} from "../actions/AdminActions/GBPActions";
import { exchangeReconnectGBPTokenAction } from "../actions/AuthActionsNew";

export interface IIntegrationData {
  _id: string;
  business_id: string;
  user_id: string;
  publishedCount: number;
  type: string;
  profile_picture: any;
  page_name: string;
  instagram_username: string;
  instagram_name: string;
  map_url: any | null;
  business_name: string;
  address: string;
  has_widgets?: boolean;
  widget_preference?: "all" | "selected";
  selected_widgets?: string[];
  channel_name?: string;
  project_id?: string;
  project_name?: string;
  project_url?: string;
  profile_picture_url?: string;
  display_name?: string;
  username?: string;
  profile_url?: string;
  creator_username?: string;
  creator_nickname?: string;
  creator_avatar_url?: string;
  duet_enabled?: boolean;
  duet_disabled?: boolean;
  stitch_enabled?: boolean;
  stitch_disabled?: boolean;
  comment_enabled?: boolean;
  comment_disabled?: boolean;
  content_type_options?: string[];
  privacy_level_options?: string[];
  reconnect_required?: boolean;
  reconnect_reason?: string | null;
  reconnect_reason_code?: string | null;
  token_last_refreshed_at?: string | null;
  token_last_refresh_error_at?: string | null;
}

interface IPaginationMeta {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface MapCheckInDashboardState {
  integrationData: IIntegrationData[];
  integrationDataLoading: boolean;
  addIntegrationDataLoading: boolean;
  removeIntegrationDataLoading: boolean;
  editProfileDataLoading: boolean;
  isAiGenerating: boolean; // Shared state for AI generation across components
  facebookPagesData: any | null; // Store Facebook pages response
  instagramPagesData: any | null; // Store Instagram pages response
  GBPReconnectPageData: any | null;
  // Subscription Details
  subscriptionDetails: any | null;
  subscriptionDetailsLoading: boolean;
  // Billing — Invoices
  invoices: any[];
  invoicesPagination: IPaginationMeta | null;
  invoicesLoading: boolean;
  // Billing — Top-ups
  topups: any[];
  topupsPagination: IPaginationMeta | null;
  topupsLoading: boolean;
}

const initialState: MapCheckInDashboardState = {
  integrationData: [],
  integrationDataLoading: false,
  addIntegrationDataLoading: false,
  removeIntegrationDataLoading: false,
  editProfileDataLoading: false,
  isAiGenerating: false,
  facebookPagesData: null,
  instagramPagesData: null,
  GBPReconnectPageData: null,
  // Subscription Details
  subscriptionDetails: null,
  subscriptionDetailsLoading: false,
  // Billing — Invoices
  invoices: [],
  invoicesPagination: null,
  invoicesLoading: false,
  // Billing — Top-ups
  topups: [],
  topupsPagination: null,
  topupsLoading: false,
};

const MapCheckInDashboardSlice = createSlice({
  name: "mapCheckIn",
  initialState,
  reducers: {
    setAiGenerating: (state, action) => {
      state.isAiGenerating = action.payload;
    },
    clearFacebookPagesData: (state) => {
      state.facebookPagesData = null;
    },
    clearInstagramPagesData: (state) => {
      state.instagramPagesData = null;
    },
    clearGBPReconnectPageData: (state) => {
      state.GBPReconnectPageData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      //get integration
      .addCase(getIntegrationAction.pending, (state) => {
        state.integrationDataLoading = true;
      })
      .addCase(getIntegrationAction.fulfilled, (state, action) => {
        state.integrationDataLoading = false;
        state.integrationData = action.payload.data;
      })
      .addCase(getIntegrationAction.rejected, (state, action) => {
        state.integrationDataLoading = false;
      })

      // connect integration
      .addCase(IntigrationAction.pending, (state) => {
        state.addIntegrationDataLoading = true;
      })
      .addCase(IntigrationAction.fulfilled, (state, action) => {
        state.addIntegrationDataLoading = false;
        state.integrationData.push(action.payload.data);
      })
      .addCase(IntigrationAction.rejected, (state) => {
        state.addIntegrationDataLoading = false;
      })

      //remove intgration
      .addCase(removeIntigrationAction.pending, (state) => {
        state.removeIntegrationDataLoading = true;
      })
      .addCase(removeIntigrationAction.fulfilled, (state, action) => {
        state.removeIntegrationDataLoading = false;

        const removedType = action.payload.data?.type;
        state.integrationData = state?.integrationData?.filter(
          (item) => item?.type !== removedType,
        );
      })
      .addCase(removeIntigrationAction.rejected, (state) => {
        state.removeIntegrationDataLoading = false;
      })

      .addCase(editMapCheckInFormAction.pending, (state) => {
        state.editProfileDataLoading = true;
      })
      .addCase(editMapCheckInFormAction.fulfilled, (state, action) => {
        state.editProfileDataLoading = false;
      })
      .addCase(editMapCheckInFormAction.rejected, (state, action) => {
        state.editProfileDataLoading = false;
      })

      // Facebook exchange token
      .addCase(facebookExchangeTokenAction.fulfilled, (state, action) => {
        state.facebookPagesData = action.payload?.data;
      })

      // Instagram exchange token (direct)
      .addCase(instagramExchangeTokenAction.fulfilled, (state, action) => {
        state.instagramPagesData = action.payload?.data;
      })

      // Instagram exchange token (via Facebook / portfolio)
      .addCase(
        instagramPortfolioExchangeTokenAction.fulfilled,
        (state, action) => {
          state.instagramPagesData = action.payload?.data;
        },
      )

      //GBP business Reconnect
      .addCase(exchangeReconnectGBPTokenAction.fulfilled, (state, action) => {
        state.GBPReconnectPageData = action.payload?.data;
      })

      // ── Invoices ──────────────────────────────────────────────────────────
      .addCase(checkinBusinessInvoicesAction.pending, (state) => {
        state.invoicesLoading = true;
      })
      .addCase(checkinBusinessInvoicesAction.fulfilled, (state, action) => {
        state.invoicesLoading = false;
        // action.payload = response.data = { success, message, status, data: { transactions, pagination } }
        const payload = action.payload as any;
        state.invoices = payload?.data?.transactions ?? [];
        state.invoicesPagination = payload?.data?.pagination ?? null;
      })
      .addCase(checkinBusinessInvoicesAction.rejected, (state) => {
        state.invoicesLoading = false;
        state.invoices = [];
        state.invoicesPagination = null;
      })

      // ── Top-ups ───────────────────────────────────────────────────────────
      .addCase(checkinBusinessTopupsAction.pending, (state) => {
        state.topupsLoading = true;
      })
      .addCase(checkinBusinessTopupsAction.fulfilled, (state, action) => {
        state.topupsLoading = false;
        const payload = action.payload as any;
        state.topups = payload?.data?.transactions ?? [];
        state.topupsPagination = payload?.data?.pagination ?? null;
      })
      .addCase(checkinBusinessTopupsAction.rejected, (state) => {
        state.topupsLoading = false;
        state.topups = [];
        state.topupsPagination = null;
      })

      // ── Subscription Details ───────────────────────────────────────────
      .addCase(checkinBusinessSubscriptionDetailsAction.pending, (state) => {
        if (!state.subscriptionDetails) {
          state.subscriptionDetailsLoading = true;
        }
      })
      .addCase(
        checkinBusinessSubscriptionDetailsAction.fulfilled,
        (state, action) => {
          state.subscriptionDetailsLoading = false;
          state.subscriptionDetails = action.payload?.data ?? action.payload;
        },
      )
      .addCase(checkinBusinessSubscriptionDetailsAction.rejected, (state) => {
        state.subscriptionDetailsLoading = false;
      });
  },
});

export const {
  setAiGenerating,
  clearFacebookPagesData,
  clearInstagramPagesData,
  clearGBPReconnectPageData,
} = MapCheckInDashboardSlice.actions;

export default MapCheckInDashboardSlice.reducer;
